/**
 * Helpers for calendar-week buckets on the date-range report charts.
 * Buckets may be partial (clipped by the selected range). All new fields are
 * optional; without them every helper falls back to the previous behavior.
 */
import { convertDateExact, formatMonthOrDate } from '@src/utils';

export interface WeekBucketMeta {
  /** First day of the (clipped) bucket, ISO yyyy-MM-dd */
  rangeStart?: string | null;
  /** Last day of the (clipped) bucket, ISO yyyy-MM-dd */
  rangeEnd?: string | null;
  /** Number of days of the week inside the selected range */
  daysInRange?: number | null;
  /** True when the bucket is clipped by the selected range */
  partial?: boolean | null;
  /** Unclipped week end (legacy field) */
  weekEnd?: string | null;
}

export const FULL_WEEK_DAYS = 7;

type YMD = { y: number; m: number; d: number };

/** Parses yyyy-MM-dd or MM/dd/yyyy without going through Date (no TZ shifts). */
const parseDate = (value?: string | null): YMD | null => {
  if (!value) return null;
  let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (m) return { y: +m[1], m: +m[2], d: +m[3] };
  m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (m) return { y: +m[3], m: +m[1], d: +m[2] };
  return null;
};

const short = (v: YMD) => `${v.m}/${v.d}`;
const full = (v: YMD) =>
  `${String(v.m).padStart(2, '0')}/${String(v.d).padStart(2, '0')}/${v.y}`;

export const isWeekBucket = (meta?: WeekBucketMeta) =>
  !!meta && (!!meta.weekEnd || (!!meta.rangeStart && !!meta.rangeEnd));

export const isPartialBucket = (meta?: WeekBucketMeta) => !!meta?.partial;

/** "Wk of 9/27"; on partial weeks a second line shows the clipped range. */
export const getWeekAxisLabel = (
  label: string,
  meta?: WeekBucketMeta
): string | string[] => {
  if (!isWeekBucket(meta)) return label;
  const start = parseDate(label) ?? parseDate(meta?.rangeStart);
  if (!start) return label;
  const base = `Wk of ${short(start)}`;
  if (!meta?.partial) return base;
  const rs = parseDate(meta.rangeStart);
  const re = parseDate(meta.rangeEnd);
  return rs && re ? [base, `${short(rs)}-${short(re)}`] : base;
};

/** Plain label for places that can't render multi-line (tables, legends). */
export const getWeekDisplayLabel = (label: string, meta?: WeekBucketMeta) => {
  const l = getWeekAxisLabel(label, meta);
  return Array.isArray(l) ? l.join(' ') : l;
};

/** Clipped range as MM/DD/YYYY - MM/DD/YYYY, falling back to legacy fields. */
export const getWeekTooltipTitle = (
  label: string,
  meta?: WeekBucketMeta
): string => {
  if (!isWeekBucket(meta)) return formatMonthOrDate(label);
  const rs = parseDate(meta?.rangeStart);
  const re = parseDate(meta?.rangeEnd);
  if (rs && re) return `${full(rs)} - ${full(re)}`;
  if (meta?.weekEnd) {
    const start = parseDate(label);
    const end = parseDate(meta.weekEnd);
    if (start && end) return `${full(start)} - ${full(end)}`;
    return `${convertDateExact(label)} - ${convertDateExact(meta.weekEnd)}`;
  }
  return formatMonthOrDate(label);
};

/** "3 of 7 days" on partial buckets, otherwise undefined. */
export const getWeekTooltipSubtitle = (
  meta?: WeekBucketMeta
): string | undefined =>
  meta?.partial && typeof meta.daysInRange === 'number'
    ? `${meta.daysInRange} of ${FULL_WEEK_DAYS} days`
    : undefined;

/** Lighter tint for partial bars/points (hex or hsl base). */
export const partialColor = (base: string) => {
  const hex = /^#([0-9a-f]{6})/i.exec(base);
  if (hex) {
    const n = parseInt(hex[1], 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, 0.45)`;
  }
  const hsl = /^hsl\((.+)\)$/.exec(base);
  if (hsl) return `hsla(${hsl[1]}, 0.45)`;
  return base;
};

/** Bucket meta of an API row, or undefined when the row is not a week bucket. */
export const pickMeta = (item: any): WeekBucketMeta | undefined => {
  const meta: WeekBucketMeta = {
    rangeStart: item?.rangeStart,
    rangeEnd: item?.rangeEnd,
    daysInRange: item?.daysInRange,
    partial: item?.partial,
    weekEnd: item?.weekEnd,
  };
  return isWeekBucket(meta) ? meta : undefined;
};
